/**
 * Service for managing file upload and processing queue
 * Handles the workflow: Upload -> Processing -> Complete/Failed
 */

class FileProcessingService {
  constructor() {
    this.processingQueue = new Map(); // fileId -> processing info
    this.listeners = new Set(); // Components listening for updates
    this.concurrentLimit = 3; // Process max 3 files simultaneously
    this.activeProcessing = new Set(); // Currently processing file IDs
    this.lastNotificationHash = null; // Track last notification to prevent duplicates
    this.notificationTimeout = null; // Debounce notifications
  }

  // Add listener for processing updates
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Generate hash of current status to detect actual changes
  getStatusHash() {
    const status = this.getProcessingStatus();
    return JSON.stringify({
      totalFiles: status.totalFiles,
      completed: status.completed,
      failed: status.failed,
      pending: status.pending,
      processing: status.processing.length,
      statuses: Array.from(this.processingQueue.values()).map(f => ({
        id: f.tempId,
        status: f.status,
        progress: f.progress
      }))
    });
  }

  // Notify all listeners of updates with debouncing and deduplication
  notifyListeners() {
    // Clear existing timeout
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }

    // Debounce notifications to prevent rapid-fire updates
    this.notificationTimeout = setTimeout(() => {
      const currentHash = this.getStatusHash();
      
      // Only notify if status actually changed
      if (currentHash !== this.lastNotificationHash) {
        this.lastNotificationHash = currentHash;
        
        const status = this.getProcessingStatus();
        this.listeners.forEach(callback => {
          try {
            callback(status);
          } catch (error) {
            console.error('Error in processing listener:', error);
          }
        });
      }
    }, 100); // 100ms debounce
  }

  // Get current processing status
  getProcessingStatus() {
    return {
      queue: Array.from(this.processingQueue.values()),
      totalFiles: this.processingQueue.size,
      processing: Array.from(this.activeProcessing),
      completed: Array.from(this.processingQueue.values()).filter(f => f.status === 'completed').length,
      failed: Array.from(this.processingQueue.values()).filter(f => f.status === 'failed').length,
      pending: Array.from(this.processingQueue.values()).filter(f => f.status === 'pending').length
    };
  }

  // Add files to processing queue
  async addFilesToQueue(files, projectType, collectionId, userInfo, projectId = null) {
    const fileEntries = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const tempId = `temp_${Date.now()}_${i}`; // Temporary ID until upload

      const entry = {
        tempId,
        file,
        fileName: file.name,
        fileSize: file.size,
        fileType: projectType,
        collectionId,
        projectId, // Include projectId for root uploads
        userInfo,
        status: 'uploading', // uploading, pending, processing, completed, failed, cancelled
        progress: 0,
        error: null,
        uploadedFileId: null,
        startTime: Date.now(),
        endTime: null
      };

      this.processingQueue.set(tempId, entry);
      fileEntries.push(entry);
    }

    this.notifyListeners();

    // Start uploading files
    this.uploadFiles(fileEntries);

    return fileEntries.map(entry => entry.tempId);
  }

  // Upload files to server
  async uploadFiles(fileEntries) {
    for (const entry of fileEntries) {
      try {
        await this.uploadSingleFile(entry);
      } catch (error) {
        console.error(`Upload failed for ${entry.fileName}:`, error);
        entry.status = 'failed';
        entry.error = error.message || 'Upload failed';
        this.notifyListeners();
      }
    }

    // Start processing uploaded files
    this.processNextInQueue();
  }

  // Upload a single file
  async uploadSingleFile(entry) {
    try {
      const formData = new FormData();
      formData.append('file', entry.file);

      // For collection uploads, include collectionId; for project root, include projectId
      if (entry.collectionId) {
        formData.append('collectionId', entry.collectionId);
      } else if (entry.projectId) {
        formData.append('projectId', entry.projectId);
      }

      formData.append('userId', entry.userInfo.userId);
      formData.append('projectType', entry.fileType);

      // Debug logging
      console.log('📤 Uploading file with params:', {
        fileName: entry.fileName,
        collectionId: entry.collectionId,
        projectId: entry.projectId,
        userId: entry.userInfo.userId,
        fileType: entry.fileType,
        hasCollectionId: !!entry.collectionId,
        hasProjectId: !!entry.projectId
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload/collection?projectType=${entry.fileType}`, {
        method: 'POST',
        body: formData,
      });

      console.log('Upload response status:', response.status, response.statusText);

      if (!response.ok) {
        // Try to read as text first to see what we're getting
        const responseText = await response.text();
        console.error('Upload error response:', responseText);
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          // If it's not JSON, use the text as error message
          throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${responseText.substring(0, 200)}`);
        }
        
        throw new Error(errorData.error || `Upload failed: ${response.status} ${response.statusText}`);
      }

      const responseText = await response.text();
      const uploadData = JSON.parse(responseText);
      
      // Update entry with uploaded file info
      entry.uploadedFileId = uploadData.files[0].id;
      entry.status = 'pending';
      entry.progress = 100; // Upload complete
      
      this.notifyListeners();

    } catch (error) {
      entry.status = 'failed';
      entry.error = error.message || 'Upload failed';
      throw error;
    }
  }

  // Process next files in queue (respecting concurrent limit)
  async processNextInQueue() {
    if (this.activeProcessing.size >= this.concurrentLimit) {
      return; // Already at concurrent limit
    }

    // Find next pending file
    const pendingFiles = Array.from(this.processingQueue.values())
      .filter(entry => entry.status === 'pending' && entry.uploadedFileId);

    if (pendingFiles.length === 0) {
      return; // No files to process
    }

    const nextFile = pendingFiles[0];
    await this.processFile(nextFile);

    // Process more files if capacity allows
    setTimeout(() => this.processNextInQueue(), 100);
  }

  // Process a single file
  async processFile(entry) {
    if (!entry.uploadedFileId) {
      entry.status = 'failed';
      entry.error = 'No uploaded file ID';
      this.notifyListeners();
      return;
    }

    try {
      entry.status = 'processing';
      this.activeProcessing.add(entry.tempId);
      this.notifyListeners();

      // Call the processing API
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/files/${entry.uploadedFileId}/process`,
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Processing failed');
      }

      const result = await response.json();
      
      // Mark as completed
      entry.status = 'completed';
      entry.endTime = Date.now();
      entry.progress = 100;
      entry.processedData = result;

    } catch (error) {
      console.error(`Processing failed for ${entry.fileName}:`, error);
      entry.status = 'failed';
      entry.error = error.message || 'Processing failed';
      entry.endTime = Date.now();
    } finally {
      this.activeProcessing.delete(entry.tempId);
      this.notifyListeners();
    }
  }

  // Cancel file processing
  cancelFile(tempId) {
    const entry = this.processingQueue.get(tempId);
    if (!entry) return false;

    if (entry.status === 'uploading' || entry.status === 'pending') {
      entry.status = 'cancelled';
      entry.error = 'Cancelled by user';
      entry.endTime = Date.now();
      
      this.activeProcessing.delete(tempId);
      this.notifyListeners();
      return true;
    }

    return false; // Cannot cancel processing files
  }

  // Retry failed file
  async retryFile(tempId) {
    const entry = this.processingQueue.get(tempId);
    if (!entry || entry.status !== 'failed') return false;

    // Reset entry status
    entry.status = entry.uploadedFileId ? 'pending' : 'uploading';
    entry.error = null;
    entry.progress = entry.uploadedFileId ? 100 : 0;
    
    this.notifyListeners();

    // Restart processing
    if (entry.uploadedFileId) {
      this.processNextInQueue();
    } else {
      // Re-upload if needed
      try {
        await this.uploadSingleFile(entry);
        this.processNextInQueue();
      } catch (error) {
        // Upload failed again, status already updated in uploadSingleFile
      }
    }

    return true;
  }

  // Remove file from queue (only if not processing)
  removeFile(tempId) {
    const entry = this.processingQueue.get(tempId);
    if (!entry) return false;

    if (entry.status === 'processing') {
      return false; // Cannot remove processing files
    }

    this.processingQueue.delete(tempId);
    this.activeProcessing.delete(tempId);
    this.notifyListeners();
    return true;
  }

  // Clear completed/failed files
  clearCompletedFiles() {
    const toRemove = [];
    for (const [tempId, entry] of this.processingQueue) {
      if (entry.status === 'completed' || entry.status === 'failed' || entry.status === 'cancelled') {
        toRemove.push(tempId);
      }
    }

    toRemove.forEach(tempId => {
      this.processingQueue.delete(tempId);
    });

    this.notifyListeners();
    return toRemove.length;
  }

  // Get file by tempId
  getFile(tempId) {
    return this.processingQueue.get(tempId);
  }

  // Get all files for a collection
  getFilesForCollection(collectionId) {
    return Array.from(this.processingQueue.values())
      .filter(entry => entry.collectionId === collectionId);
  }
}

// Create singleton instance
const fileProcessingService = new FileProcessingService();

export default fileProcessingService;